// api/chartOfAccountsAPI.js
import axiosInstance from './axiosConfig';
import { getAccessToken } from './axiosConfig';
import { requireAuthToken } from '../utils/authUtils';

const chartOfAccountsAPI = {
  search: async (query) => {
    try {
      requireAuthToken(getAccessToken);

      const response = await axiosInstance.get("/chart-of-accounts/search", {
        params: { q: query },
      });
      return response.data;
    } catch (error) {
      console.error("Chart of Accounts Search Error:", error);
      throw (
        error.response?.data?.message ||
        error.message ||
        "Failed to search chart of accounts"
      );
    }
  },

  getAll: async (tenantId, storeId, params = {}, axiosOptions = {}) => {
    try {
      requireAuthToken(getAccessToken);
      const allParams = {
        ...(tenantId && typeof tenantId === 'string' ? { tenant: tenantId } : {}),
        ...(storeId   && typeof storeId   === 'string' ? { store:  storeId  } : {}),
        ...params,
      };

      const response = await axiosInstance.get('/chart-of-accounts', {
        params: allParams,
        ...axiosOptions,
      });
      return response.data;
    } catch (error) {
      console.error('Chart of Accounts API Error:', error);
      throw error.response?.data?.message || error.message || 'Failed to fetch chart of accounts';
    }
  },

  // Alternative method that accepts just tenant and store
  getByTenantAndStore: async (tenantId, storeId) => {
    try {
      requireAuthToken(getAccessToken);
      const response = await axiosInstance.get('/chart-of-accounts', { 
        params: { tenant: tenantId, store: storeId } 
      });
      return response.data;
    } catch (error) {
      console.error('Chart of Accounts API Error:', error);
      throw error.response?.data?.message || error.message || 'Failed to fetch chart of accounts';
    }
  },

  getById: async (id) => {
    try {
      requireAuthToken(getAccessToken);
      const response = await axiosInstance.get(`/chart-of-accounts/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch account';
    }
  },

  create: async (accountData, axiosOptions = {}) => {
    try {
      requireAuthToken(getAccessToken);
      const response = await axiosInstance.post('/chart-of-accounts', accountData, axiosOptions);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to create account';
    }
  },

  update: async (id, accountData) => {
    try {
      requireAuthToken(getAccessToken);
      const response = await axiosInstance.put(`/chart-of-accounts/${id}`, accountData);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to update account';
    }
  },

  delete: async (id) => {
    try {
      requireAuthToken(getAccessToken);
      const response = await axiosInstance.delete(`/chart-of-accounts/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to delete account';
    }
  },

  // Additional methods
  getTree: async (tenantId, storeId) => {
    try {
      requireAuthToken(getAccessToken);
      const response = await axiosInstance.get('/chart-of-accounts/tree', {
        params: { tenant: tenantId, store: storeId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch account tree';
    }
  },

  getByType: async (typeId, tenantId, storeId) => {
    try {
      requireAuthToken(getAccessToken);
      const response = await axiosInstance.get(`/chart-of-accounts/type/${typeId}`, {
        params: { tenant: tenantId, store: storeId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch accounts by type';
    }
  },


  updateBalance: async (id, balance) => {
    try {
      requireAuthToken(getAccessToken);
      const response = await axiosInstance.put(`/chart-of-accounts/${id}/balance`, { balance });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to update account balance';
    }
  },

  getNextNumber: async (accountTypeId) => {
    requireAuthToken(getAccessToken);
    const res = await axiosInstance.get(`/chart-of-accounts/next-number/${accountTypeId}`);
    return res.data.data.nextNumber;
  },


};

export default chartOfAccountsAPI;